import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { ApiService } from './api.service';
import {
  ContractCandidateListRequest,
  ContractCandidateListResponse,
  MapDocumentRequest,
  RequestMandatoryDocsPayload
} from '../types/Contract';

/**
 * Service for Contract Candidate APIs.
 * Handles 6 new contractCandidate controller endpoints + 6 existing jobs/ document endpoints.
 */
@Injectable({ providedIn: 'root' })
export class ContractCandidateService {

  private readonly CANDIDATE_PATH = 'contractCandidate';

  constructor(private apiService: ApiService) {}

  // ============================================================================
  // 6 NEW APIs — contractCandidate controller
  // ============================================================================

  /**
   * API 4.1: List candidates with filters and pagination.
   * POST /contractCandidate/list
   */
  listCandidates(request: ContractCandidateListRequest): Observable<ContractCandidateListResponse> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/list`, request);
  }

  /**
   * API 4.2: Get single candidate mapping with full details + document configuration.
   * GET /contractCandidate/{contractCandidateId}
   */
  getCandidateDetail(contractCandidateId: string): Observable<any> {
    return this.apiService.query(`${this.CANDIDATE_PATH}/${contractCandidateId}`);
  }

  /**
   * API 4.3: Create contract candidate mapping (triggered when candidate reaches OFFERED).
   * POST /contractCandidate/create
   */
  createCandidate(payload: any): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/create`, payload);
  }

  /**
   * API 4.4: Update candidate status (OFFERED → ONBOARDED).
   * PUT /contractCandidate/update-status
   */
  updateCandidateStatus(contractCandidateId: string, candidateStatus: string): Observable<any> {
    return this.apiService.updatePut(`${this.CANDIDATE_PATH}/update-status`, {
      contractCandidateId,
      candidateStatus
    });
  }

  /**
   * API 4.5: Sync document counts from DocumentConfiguration into contract candidate record.
   * Called after every document operation (upload/verify/delete/map).
   * PUT /contractCandidate/sync-documents/{contractCandidateId}
   */
  syncDocuments(contractCandidateId: string): Observable<any> {
    return this.apiService.updatePut(`${this.CANDIDATE_PATH}/sync-documents/${contractCandidateId}`, {});
  }

  /**
   * API 4.6: Map a document from supplier's DocumentConfiguration to customer's.
   * No re-upload — references the same S3 fileId.
   * POST /contractCandidate/map-document
   */
  mapDocument(request: MapDocumentRequest): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/map-document`, request);
  }

  // ============================================================================
  // 6 EXISTING APIs — jobs/ prefix (reused from Job Module)
  // ============================================================================

  /**
   * API 5.1: Demand/configure mandatory documents for a candidate.
   * POST /requestMandatoryDocuments
   */
  requestMandatoryDocuments(payload: RequestMandatoryDocsPayload): Observable<any> {
    return this.apiService.create('jobs/requestMandatoryDocuments', payload);
  }

  /**
   * API 5.2: Get DocumentConfiguration with all document details, expiry check, and counts.
   * POST /getContractDocumentDetails
   */
  getDocumentDetails(jobId: string, candidateId: string): Observable<any> {
    return this.apiService.create('jobs/getContractDocumentDetails', { jobId, candidateId });
  }

  /**
   * API 5.3: Upload a candidate document.
   * POST /uploadCandidateDocument (multipart/form-data)
   */
  uploadDocument(formData: FormData): Observable<any> {
    return this.apiService.create('jobs/uploadCandidateDocument', formData);
  }

  /**
   * API 5.4: Verify or reject an uploaded document.
   * POST /updateDocumentStatus
   */
  updateDocumentStatus(payload: any): Observable<any> {
    return this.apiService.create('jobs/updateDocumentStatus', payload);
  }

  /**
   * API 5.5: Delete an uploaded document.
   * PUT /deleteDocumentRecord
   */
  deleteDocument(payload: any): Observable<any> {
    return this.apiService.updatePut('jobs/deleteDocumentRecord', payload);
  }

  /**
   * API 5.6: Update/replace an existing uploaded document.
   * PUT /updateCandidateDocument (multipart/form-data)
   */
  updateDocument(formData: FormData): Observable<any> {
    return this.apiService.updatePut('jobs/updateCandidateDocument', formData);
  }

  /**
   * Event Log (cursor-paginated via searchAfterKey).
   * POST /contractCandidate/{contractCandidateId}/event-logs/search
   * Body: { size, sortBy, sortDir, searchAfterKey }
   * Response: { eventLogs: [{action, by, on, reason?, documents?: [...]}],
   *             totalCount, size, hasMore, searchAfterKey, sortBy, sortDir }
   */
  getEventLogs(
    contractCandidateId: string,
    size: number = 10,
    sortBy: 'action' | 'by' | 'date' | 'time' = 'date',
    sortDir: 'asc' | 'desc' = 'asc',
    searchAfterKey: any[] | null = null
  ): Observable<any> {
    const sortKey = sortBy === 'time' ? 'date' : sortBy;
    return this.apiService.create(`${this.CANDIDATE_PATH}/${contractCandidateId}/event-logs/search`, {
      size,
      sortBy: sortKey,
      sortDir,
      searchAfterKey
    });
  }

  /**
   * Find business connections (customers/suppliers)
   * POST /contractCandidate/findConnections
   *
   * limit/searchAfterKey follow the same cursor-pagination convention as
   * business/findBusinessCustomers (see GigsumoService.updateBusinessCustomers):
   * pass the previous response's searchAfterKey back in to fetch the next
   * page. Omit them to fetch the full (legacy, unpaginated) list.
   */
  findConnections(businessId: string, roleFilter?: string, limit?: number, searchAfterKey?: any, searchContent?: string): Observable<any> {
    const payload: any = { businessId };
    if (roleFilter) {
      payload.roleFilter = roleFilter;
    }
    if (limit !== undefined) {
      payload.limit = limit;
    }
    if (searchAfterKey !== undefined && searchAfterKey !== null) {
      payload.searchAfterKey = searchAfterKey;
    }
    if (searchContent) {
      payload.searchContent = searchContent;
    }
    return this.apiService.create(`${this.CANDIDATE_PATH}/findConnections`, payload);
  }

  /**
   * Returns the Customer filter dropdown options for the Agreement Document page.
   * Server queries ContractCandidate rows where candidateStatus is OFFER+ for the
   * (customer, supplier) pair, returns distinct behalfOfBusinessId values enriched
   * with names. The "own" entry (behalfOfBusinessId = null) appears only if at least
   * one ContractCandidate exists for the pair with no behalfOf.
   * POST /contractCandidate/contextFilters
   */
  getContextFilters(customerBusinessId: string, supplierBusinessId: string): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/contextFilters`, {
      customerBusinessId,
      supplierBusinessId
    });
  }

  /**
   * Validate B2B connection before opening payrate modal
   * POST /contractCandidate/validateB2bConnection
   */
  validateB2bConnection(businessId: string, jobId: string, candidateId: string, candidateCurrentStatus?: string): Observable<any> {
    const userId = localStorage.getItem('userId') || '';
    const payload: any = { businessId, jobId, candidateId, userId };
    if (candidateCurrentStatus) {
      payload.candidateCurrentStatus = candidateCurrentStatus;
    }
    return this.apiService.create(`${this.CANDIDATE_PATH}/validate-b2b-connection`, payload);
  }

  // ============================================================================
  // Contract Management Extension APIs (Scenarios 1-3)
  // ============================================================================

  /**
   * Validate that the candidate's business has a paid plan before allowing offer acceptance.
   * Called BEFORE updateCandidateStatus when status is OFFER-ACCEPTED.
   * POST /contractCandidate/validate-business-plan
   *
   * Success (paid plan): { code: "00000", data: { businessId, subscriptionType } }
   * Failure (free plan): { code: "10004", message: "Business plan required..." }
   */
  validateBusinessPlan(candidateId: string = '', businessId: string = ''): Observable<any> {
    // Pass candidateId to check the candidate OWNER's plan (offer-accept), or businessId to check
    // a specific business's plan (job owner advancing a candidate). Backend prefers businessId.
    return this.apiService.create(`${this.CANDIDATE_PATH}/validate-business-plan`, { candidateId, businessId });
  }

  /**
   * Auto-link an external business from a user's WorkExperience (current organization).
   * Enhanced /externalBusiness/create — when userId is provided, auto-populates from WorkExperience.
   * POST /externalBusiness/create
   */
  createExternalBusinessAutoLink(businessId: string, userId: string, relationshipType: string): Observable<any> {
    return this.apiService.create('externalBusiness/create', { businessId, userId, relationshipType });
  }

  /**
   * Search applicants who applied to this business's jobs — for auto-link modal.
   * POST /contractCandidate/search-applicants
   */
  searchApplicants(businessId: string, searchContent: string, limit = 20): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/search-applicants`, { businessId, searchContent, limit });
  }

  /**
   * Auto-link an external business via candidateId (resolves to userId internally).
   * POST /externalBusiness/create
   */
  createExternalBusinessByCandidateId(businessId: string, candidateId: string, relationshipType: string): Observable<any> {
    return this.apiService.create('externalBusiness/create', { businessId, candidateId, relationshipType });
  }

  /**
   * Scenario 4: Get an employee's contract roles.
   * GET /business/employee/getContractRoles?businessId=...&userId=...
   */
  getContractRoles(businessId: string, userId: string): Observable<any> {
    return this.apiService.query(`business/employee/getContractRoles?businessId=${businessId}&userId=${userId}`);
  }

  /**
   * Scenario 4: Assign contract roles to an employee.
   * PUT /business/employee/updateContractRoles
   */
  updateContractRoles(businessId: string, targetUserId: string, contractRoles: string[]): Observable<any> {
    return this.apiService.updatePut('business/employee/updateContractRoles', { businessId, targetUserId, contractRoles });
  }

  /**
   * Accept offer with business plan validation (Scenarios 2 + 3 combined).
   *
   * Flow:
   * 1. Call validate-business-plan — if code 10004, emit error (caller shows "Buy Plan" Swal)
   * 2. If validated, call updateCandidateStatus with OFFER-ACCEPTED
   * 3. Response may include autoCreatedExternalBusiness (Corp-to-Corp auto-creation)
   *
   * Returns the updateCandidateStatus response on success,
   * or the validate-business-plan error response if validation fails.
   */
  acceptOfferWithValidation(candidateId: string, contractCandidateId: string): Observable<any> {
    return this.validateBusinessPlan(candidateId).pipe(
      switchMap((valRes: any) => {
        if (valRes.code === '10004') {
          return of(valRes);
        }
        if (valRes.code !== '00000') {
          return of(valRes);
        }
        return this.updateCandidateStatus(contractCandidateId, 'OFFER-ACCEPTED');
      })
    );
  }

  // ============================================================================
  // Job Seeker Scenarios — Supplier Validation + Absorption
  // ============================================================================

  /**
   * Validate that a supplier profile exists for a Corp-to-Corp candidate applying to a sub-contracted job.
   * Called BEFORE job application when engagementType is Corp to Corp and job is sub-contracted.
   * POST /contractCandidate/validate-supplier-profile
   *
   * Success: { code: "00000" }
   * Failure: { code: "10005", message: "No supplier profile found..." }
   */
  validateSupplierProfile(candidateId: string, jobId: string): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/validate-supplier-profile`, { candidateId, jobId });
  }

  /**
   * Freeze all other active applications for a candidate (except the specified job).
   * Called at OFFER-ACCEPTED for non-Corp-to-Corp candidates.
   * POST /contractCandidate/freeze-other-applications
   */
  freezeOtherApplications(candidateId: string, jobId: string): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/freeze-other-applications`, { candidateId, jobId });
  }

  /**
   * Permanently invalidate all other applications + send notifications.
   * Called at ONBOARDED for non-Corp-to-Corp candidates.
   * POST /contractCandidate/invalidate-other-applications
   */
  invalidateOtherApplications(candidateId: string, jobId: string): Observable<any> {
    return this.apiService.create(`${this.CANDIDATE_PATH}/invalidate-other-applications`, { candidateId, jobId });
  }

  // ============================================================================
  // Shared UI helpers — B2B-missing popup
  // ============================================================================

  /**
   * Show the "Business connection required" popup used by both the Applicant page
   * (job-card jobupdatestatus) and the Contract Candidate menu (Initiate Contract gate)
   * when validate-b2b-connection returns 10002 or 10003.
   *
   * Returns a Promise resolving to the SweetAlert result. The caller inspects
   * `result.isConfirmed` (true = user clicked "Go to Business Contacts") and
   * navigates with their own Router instance so the service stays Router-free.
   *
   * @param code        '10002' (no B2B) or '10003' (no B2B + non-SUPERADMIN)
   * @param backendMsg  Optional message from the backend; used when present.
   */
  showNoB2bPopup(code: string, backendMsg?: string): Promise<SweetAlertResult> {
    let title = 'Business connection required';
    let html = backendMsg || 'A B2B connection between the two businesses is required to proceed.';
    let showCta = true;

    if (code === '10003') {
      title = 'Permission required';
      html = backendMsg
        || 'You do not have the permissions to initiate a contract. Please contact your business administrator.';
      showCta = false; // role issue, not a B2B issue — no navigation CTA
    } else if (code === '10002') {
      title = 'Business connection required';
      html = backendMsg
        || 'Business connection required to proceed. Go to Business Contacts to invite the other business.';
      showCta = true;
    } else if (code === '10004') {
      // Locked 2026-06-15 — B2B invite already sent and pending the peer's acceptance.
      // Distinct from 10002 ("no contact row at all") so the UI never re-opens the
      // Add Business form / re-sends an invite. Just inform the user and wait.
      title = 'B2B connection pending';
      html = backendMsg
        || 'A B2B connection request has already been sent and is awaiting acceptance from the other business. Please wait for them to accept before initiating the contract.';
      showCta = false;
    }

    const isInfoIcon = code === '10003' || code === '10004';
    return Swal.fire({
      icon: isInfoIcon ? 'info' : 'warning',
      title,
      html,
      showCancelButton: showCta,
      cancelButtonText: 'OK',
      confirmButtonText: showCta ? 'Go to Business Contacts' : 'OK',
      reverseButtons: true,
      allowOutsideClick: false
    });
  }

  /**
   * Show the "Mandatory Documentation Process" popup used whenever a candidate is
   * moved to PRE-ONBOARDED / ONBOARDED before the document process has been
   * initiated and all mandatory documents uploaded.
   *
   * Backend returns 99996 (SocialplatformConstants.INTERRUPTED) with a technical
   * message ("Cannot move candidate to PRE-ONBOARDED status. All mandatory
   * documents must be uploaded first."). That wording told the user WHAT failed
   * but not WHAT TO DO, and the generic "Validation Failed" title read like a
   * system error. Per owner directive 2026-09-21 both are replaced here with a
   * single business-facing message pointing at the next action, so the backend
   * message is deliberately NOT surfaced.
   *
   * Used by the Contract Center → Candidates status dropdown (validate-b2b-connection)
   * and by candidates/updatejobAppliedStatus, so both paths read identically.
   */
  showMandatoryDocumentPopup(): Promise<SweetAlertResult> {
    return Swal.fire({
      icon: 'error',
      title: 'Mandatory Documentation Process',
      text: 'The document process is mandatory before a candidate is moved to Pre-Onboard or Onboard statuses. Please initiate the document process.',
      confirmButtonText: 'OK',
      allowOutsideClick: false
    });
  }
}
